import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

interface AppLifecycleHandlerProps {
  onResume?: () => void;
  onPause?: () => void;
}

/**
 * Component that handles native app lifecycle events (resume/pause).
 * Used to sync state like notification badge counts when app returns to foreground.
 */
export const AppLifecycleHandler = ({ 
  onResume,
  onPause 
}: AppLifecycleHandlerProps) => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    let resumeHandle: Awaited<ReturnType<typeof App.addListener>> | null = null;
    let pauseHandle: Awaited<ReturnType<typeof App.addListener>> | null = null;

    const setupListeners = async () => {
      resumeHandle = await App.addListener('resume', () => {
        console.log('[AppLifecycle] App resumed from background');
        onResume?.();
      });

      pauseHandle = await App.addListener('pause', () => {
        console.log('[AppLifecycle] App paused (going to background)');
        onPause?.();
      });
    };

    setupListeners();

    return () => {
      resumeHandle?.remove();
      pauseHandle?.remove();
    };
  }, [onResume, onPause]);

  return null;
};

export default AppLifecycleHandler;
